import type { BsModalRef } from 'ngx-bootstrap/modal';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { NgSelectModule } from '@ng-select/ng-select';

import type { FormAdditionalSettingsData } from '@sites/entity-core';

import type { SitePageEntity } from '@common/types/site-page.type';
import type { Domain } from '@common/types/domain.type';
import type { DeviceMode } from '@common/types/layout.type';
import { type PromoCode, PromoCodeDiscountType } from '@common/types/promo-codes.type';
import { EActionAfterConfirm, ECaptchaLimits } from '@common/enums';

import type { SubscriptionMessageModalPayload } from '../sp-form-additional-settings/src/subscription-message-modal/subscription-message-modal.component';
import { SubscriptionMessageModalComponent } from '../sp-form-additional-settings/src/subscription-message-modal/subscription-message-modal.component';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { SpControlHandleComponent } from '../../ui/sp-control-handle/src/sp-control-handle.component';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { SpSwitchComponent } from '../../ui/sp-switch/src/sp-switch.component';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { HtmlToTextPipe } from '../../../common/pipes/html-to-text.pipe';

/**
 * Signal-form editor for the form widget's post-submit behaviour — the
 * signal twin of the FormGroup-based `sp-form-additional-settings`. Mirrors
 * that template (action-after-confirm select, success-message modal, target
 * link + page picker, update-contact / corporate-email / captcha switches,
 * payment-form promo-code block) with the value-in
 * (`FormAdditionalSettingsData` from `@sites/entity-core`) /
 * `valueChangeEvent`-out contract.
 *
 * The observable inputs of the original (`pages$`-style) are plain arrays
 * here — the host wrapper feeds resolved values.
 */
@Component({
    selector: 'sp-form-additional-settings-signal',
    templateUrl: './sp-form-additional-settings-signal.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FormsModule,
        TranslateModule,
        BsDropdownModule,
        TooltipModule,
        NgSelectModule,
        SpControlHandleComponent,
        SpSwitchComponent,
        HtmlToTextPipe,
    ],
})
export class SpFormAdditionalSettingsSignalComponent {
    private readonly modalService = inject(BsModalService);

    public readonly settings = input<FormAdditionalSettingsData | null>(null);
    public readonly isHasSuccessPageOption = input<boolean>(false);
    public readonly pages = input<SitePageEntity[]>([]);
    public readonly siteDomains = input<Domain[]>([]);
    public readonly baseHref = input<string>('');
    public readonly deviceMode = input<DeviceMode | null>(null);
    public readonly addChatBotToForm = input<boolean>(false);
    public readonly isPaymentForm = input<boolean>(false);
    public readonly isEduForm = input<boolean>(false);
    public readonly promoCodes = input<PromoCode[]>([]);

    public readonly valueChangeEvent = output<FormAdditionalSettingsData>();

    private modalRef: BsModalRef = null;

    public readonly EActionAfterConfirm = EActionAfterConfirm;
    public readonly ECaptchaLimits = ECaptchaLimits;
    public readonly promoCodeDiscountType = PromoCodeDiscountType;

    private readonly model = signal<FormAdditionalSettingsData | null>(null);

    public readonly actionAfterConfirmType = computed(() => this.model()?.actionAfterConfirmType);
    public readonly subscriptionMessage = computed(() => this.model()?.subscriptionMessage ?? '');
    public readonly openLink = computed(() => this.model()?.openLink ?? '');
    public readonly updateExistingContact = computed(() => Boolean(this.model()?.updateExistingContact));
    public readonly isEnabledCaptcha = computed(() => Boolean(this.model()?.isEnabledCaptcha));
    public readonly captchaLimit = computed(() => this.model()?.captchaLimit ?? null);
    public readonly isEnableCorporateEmail = computed(() => Boolean(this.model()?.isEnableCorporateEmail));
    public readonly isEnabledPromoCode = computed(() => Boolean(this.model()?.promoCodeSettings?.isEnabled));
    public readonly labelPromoCode = computed(() => this.model()?.promoCodeSettings?.label ?? '');
    public readonly placeholderPromoCode = computed(() => this.model()?.promoCodeSettings?.placeholder ?? '');
    public readonly selectedPromoCode = computed(() => this.model()?.promoCodeSettings?.selectedPromoCode ?? []);

    public readonly selectedPromoValues = computed<string>(() => {
        const selected = this.selectedPromoCode();
        const promoCodes = this.promoCodes();
        if (!selected?.length || !promoCodes?.length) {
            return '';
        }

        const discounts: string[] = [];
        selected.forEach((id) => {
            const promoCode = promoCodes.find((code) => code.id === Number(id));
            if (promoCode) {
                (promoCode.discounts ?? []).forEach((discount) => {
                    discounts.push(
                        `${discount.value} ${
                            discount.type === this.promoCodeDiscountType.money ? ' ' + discount.currency : '%'
                        }`
                    );
                });
            }
        });

        return discounts.join(', ');
    });

    /**
     * First discount of a promo code, formatted for the picker row. Returns an empty
     * string when the code carries no discounts — the API shape allows it, and a
     * blind `discounts[0].value` in the template would break the whole option list.
     */
    public promoCodeDiscountLabel(promoCode: PromoCode): string {
        const discount = promoCode.discounts?.[0];

        if (!discount) {
            return '';
        }

        return `${discount.value}${discount.type === this.promoCodeDiscountType.money ? ' ' + discount.currency : ' %'}`;
    }

    constructor() {
        effect(() => {
            const incoming = this.settings();
            if (!incoming) return;
            this.model.set(incoming);
        });
    }

    // --- Setters (template event handlers) ---

    public onActionAfterConfirmChange(event: Event): void {
        this.patch({
            actionAfterConfirmType: (event.target as HTMLSelectElement)
                .value as FormAdditionalSettingsData['actionAfterConfirmType'],
        });
    }

    public onOpenLinkChange(event: Event): void {
        this.patch({ openLink: (event.target as HTMLInputElement).value });
    }

    public setOpenLink(page: SitePageEntity): void {
        this.patch({ openLink: page.url });
    }

    public setUpdateData(value: boolean): void {
        this.patch({ updateExistingContact: value });
    }

    public handleIsEnabledCaptcha(value: boolean): void {
        this.patch({ isEnabledCaptcha: value });
    }

    public onCaptchaLimitChange(event: Event): void {
        this.patch({ captchaLimit: Number((event.target as HTMLSelectElement).value) });
    }

    public handleIsEnabledCorporateEmail(value: boolean): void {
        this.patch({ isEnableCorporateEmail: value });
    }

    public setEnabledPromoCode(value: boolean): void {
        this.patchPromoCode({ isEnabled: value });
    }

    public onLabelPromoCodeChange(event: Event): void {
        this.patchPromoCode({ label: (event.target as HTMLInputElement).value });
    }

    public onPlaceholderPromoCodeChange(event: Event): void {
        this.patchPromoCode({ placeholder: (event.target as HTMLInputElement).value });
    }

    public setPromoCode(value: number[]): void {
        this.patchPromoCode({ selectedPromoCode: value });
    }

    public showSubscriptionMessageModal(): void {
        this.modalRef = this.modalService.show(SubscriptionMessageModalComponent, {
            initialState: {
                subscriptionMessage: this.subscriptionMessage(),
                baseHref: this.baseHref(),
                deviceMode: this.deviceMode(),
            },
        });

        this.modalRef.content.saveData.subscribe((data: SubscriptionMessageModalPayload) => {
            this.patch({ subscriptionMessage: data.cachedText });
        });
    }

    private patch(patch: Partial<FormAdditionalSettingsData>): void {
        const current = this.model();
        if (!current) return;
        const next = { ...current, ...patch };
        this.model.set(next);
        this.valueChangeEvent.emit(next);
    }

    private patchPromoCode(patch: Partial<NonNullable<FormAdditionalSettingsData['promoCodeSettings']>>): void {
        const current = this.model();
        if (!current) return;
        const promoCodeSettings = {
            isEnabled: null,
            label: null,
            placeholder: null,
            selectedPromoCode: null,
            ...current.promoCodeSettings,
            ...patch,
        };
        this.patch({ promoCodeSettings });
    }
}
